import _ from 'lodash';
import React from 'react';
import { createSelector } from 'reselect';
import {
  calculateAssignmentsTotals,
  extractSettingAs,
  getToolIds,
  getTotals,
  getUngradedStatus,
} from '@macmillan-learning/gradebook-shared';
import { GRADING_TYPE } from '../utils/constants';

import NameColumnSorter from '../components/InstructorGradebookContainer/Components/Sorter/NameColumnSorter';

const getGradebookStudents = (state) => state.gradebook.students;
const getGradebookCategories = (state) => state.gradebook.categories;
const getGradebookCategoryWeights = (state) => state.gradebook.categoryWeights;
const getGradebookAssignments = (state) => state.gradebook.assignments;
const getGradebookAverages = (state) => state.gradebook.averages;
const getGradebookSettings = (state) => state.gradebook.settings;
const getCollapsedCategoryIds = (state) => state.gradebook.collapsedCategoryIds;
export const selectGradebookCourse = (state) => state.gradebook.course;

export const getUngradedAssignments = (assignments) =>
  _.filter(assignments, (ass) => ass.gradebook_category_id === -1);

export const getCategoryAssignments = (category, assignments) => {
  if (category.is_default) {
    // if is a default category then should accept 2 conditions or is the same id or is 0 in the case that
    // gradebook_category_id is equals to 0 is required overwrite the gradebook_category_id
    return _.filter(
      assignments,
      (assignment) =>
        [category.id, 0].includes(assignment.gradebook_category_id) &&
        assignment.grading_type !== GRADING_TYPE.UNGRADED,
    ).map((assigment) => ({
      ...assigment,
      gradebook_category_name: category.name,
      gradebook_category_id: category.id,
    }));
  }

  if (category.is_default_ungraded) {
    // if is a default ungraded category then override the category info to prevent show ungraded assignment in other
    // categories
    return _.filter(
      assignments,
      (assignment) => assignment.grading_type === GRADING_TYPE.UNGRADED,
    ).map((assigment) => ({
      ...assigment,
      gradebook_category_name: category.name,
      gradebook_category_id: category.id,
    }));
  }

  return _.filter(
    assignments,
    (assignment) =>
      assignment.gradebook_category_id === category.id &&
      assignment.grading_type !== GRADING_TYPE.UNGRADED,
  );
};

export const sortAssignmentsByDueDate = (assignments) => {
  return _.sortBy(assignments, (assignment) => {
    if (assignment.assignment_due_at) {
      return assignment.assignment_due_at;
    }
    return _.get(
      _.find(assignment.exceptions, {
        assignment_id: assignment.id,
        exception_type: 'assignment',
      }),
      'exception_value.due_date',
    );
  });
};

export const getStudentCategoryAssignmentsWithAggregate = (
  category,
  student,
  getCategoryAssignmentsFn,
  settings = undefined,
) => {
  const categoryAssignments = _.sortBy(
    getCategoryAssignmentsFn(category, _.get(student, 'assignments', [])),
    'name',
  );
  categoryAssignments.forEach((cat) => {
    cat.drop_lowest_num_grades = category.drop_lowest_num_grades;
  });
  const unfilteredCategoryAssignments = getCategoryAssignmentsFn(
    category,
    _.get(student, 'unfilteredAssignments', []),
  );
  const notDroppedAssignments = _.filter(
    unfilteredCategoryAssignments,
    (o) => !o.isDropped,
  );
  const { pointsEarned, pointsPossible, grade } = calculateAssignmentsTotals(
    notDroppedAssignments,
  );
  const weight = category && category.weight;
  const categoryTotal = isFinite(pointsEarned / pointsPossible)
    ? pointsEarned / pointsPossible
    : 0;
  const weightedCategoryTotal = categoryTotal * weight;
  // This has to be pushed on the end of the array to render as the last column
  const aggregateAssignment = {
    id: `${category.id}`,
    headerContent: 'Category Total',
    studentPoints: pointsEarned,
    assigned_points: pointsPossible,
    gradebook_category_id: category.id,
    gradebook_category_name: category.name,
    weight,
    weightedCategoryTotal,
    isAggregateCell: true,
    studentGrade: grade,
    isUngraded: category.is_default_ungraded,
  };

  // if is a ungraded default category then set the numCompleted
  if (category.is_default_ungraded) {
    aggregateAssignment.numCompleted = _.filter(
      _.map(categoryAssignments, (assignment) =>
        getUngradedStatus(assignment, settings),
      ),
      (a) => a === 'completed',
    ).length;
  }
  const sortedNonAggregate = sortAssignmentsByDueDate(categoryAssignments);
  sortedNonAggregate.push(aggregateAssignment);
  return sortedNonAggregate;
};

export const getStudentCategoryAssignments = (
  category,
  student,
  settings = undefined,
) =>
  getStudentCategoryAssignmentsWithAggregate(
    category,
    student,
    getCategoryAssignments,
    settings,
  );

const getStudentLmsId = (student) => {
  if (_.isEmpty(student.thirdPartyUserId)) return 'Not entered';
  return student.thirdPartyUserId;
};

const getEmail = (student) => {
  if (_.isEmpty(student.email)) return 'n/a';
  return student.email;
};

// TODO: weird to be making components in a selectors file
const getAssignmentsRow = (
  categories,
  assignments,
  student,
  requireStudentId,
) => {
  const firstTwoCells = [
    { headerContent: <NameColumnSorter search={window.location.search} /> },
  ];
  if (requireStudentId === 'true')
    firstTwoCells.push({ isStudentIdColumn: true });
  firstTwoCells.push({
    headerContent: 'Course Total',
    isCourseTotalColumn: true,
    isAggregateCell: true,
  });

  // fix exceptions, since the hack of using firstStudent erroneously filters out other exceptions
  const mappedCategories = _.flatten(
    _.map(categories, (cat) =>
      _.map(getStudentCategoryAssignments(cat, student), (asgn) => ({
        ...asgn,
        Exceptions: _.get(
          _.find(assignments, { id: asgn.id }),
          'exceptions',
          [],
        ),
      })),
    ),
  );
  const hasAssignments =
    Array.isArray(mappedCategories) && mappedCategories.length > 1;

  return {
    assignmentsRow: _.concat(firstTwoCells, mappedCategories),
    hasAssignments,
  };
};

// getCategoriesRow maps 3 properties onto each assignment/grade object in the array
// isFirstCell - used to determine if we should show the category title in the grade book table (1st row)
// isMiddleCell - used to determine if we should show an empty cell in the grade book table (1st row)
// isLastCell - used to determine if this is the last category cell in the grade book table (1st row)
export const getCategoriesRow = (
  assignmentsRow,
  weights,
  leftGridColumnCount,
) => {
  let currentCategoryId = null;
  return assignmentsRow.map((asgn, i) => {
    const foundWeights = weights.find(
      (testWeight) =>
        asgn.grading_type !== GRADING_TYPE.UNGRADED &&
        testWeight.category_id === asgn.gradebook_category_id,
    );
    asgn.weight = foundWeights ? foundWeights.weight : 0;
    if (i < leftGridColumnCount) {
      if (i === leftGridColumnCount - 1) {
        return {
          ...asgn,
          isLastCell: true,
          isAggregateCell: true,
        };
      }
      return asgn;
    }
    const categoryHasAssignments = assignmentsRow.filter(
      (asg) => asg.gradebook_category_id === asgn.gradebook_category_id,
    );
    if (
      Array.isArray(categoryHasAssignments) &&
      categoryHasAssignments.length === 1
    ) {
      return {
        ...asgn,
        isSingleCell: true,
      };
    }
    if (
      _.isNumber(asgn.gradebook_category_id) &&
      !_.isNumber(currentCategoryId) &&
      !asgn.isAggregateCell
    ) {
      currentCategoryId = asgn.gradebook_category_id;
      return {
        ...asgn,
        isFirstCell: true,
      };
    } else if (_.isNumber(currentCategoryId) && asgn.isAggregateCell) {
      currentCategoryId = null;
      return {
        ...asgn,
        isLastCell: true,
      };
    } else if (_.isNumber(currentCategoryId) && !asgn.isAggregateCell) {
      return {
        ...asgn,
        isMiddleCell: true,
      };
    }
    return asgn;
  });
};

const getStudentsRow = ({
  categories,
  weights,
  student,
  requireStudentId,
  settings,
}) => {
  const {
    studentId,
    last_name: lastName,
    first_name: firstName,
    isBrokenSyncReefGrades,
  } = student;

  const assignments = _.filter(
    student.unfilteredAssignments,
    (asgn) => asgn.grading_type !== GRADING_TYPE.UNGRADED,
  );
  const { grade, pointsEarned, pointsPossible } = getTotals(
    assignments,
    weights,
    settings,
    categories,
  );
  const studentInfoAndPointsColumns = [
    {
      studentId,
      firstName,
      lastName,
      isBrokenSyncReefGrades,
      email: getEmail(student),
      isStudentNameColumn: true,
    },
  ];
  if (requireStudentId === 'true') {
    studentInfoAndPointsColumns.push({
      studentId,
      isStudentIdColumn: true,
      headerContent: getStudentLmsId(student),
    });
  }
  studentInfoAndPointsColumns.push({
    grade,
    studentId,
    pointsEarned,
    pointsPossible,
    isAggregateCell: true,
    isCourseTotalColumn: true,
  });
  const realCats = _.flatten(
    _.map(categories, (cat) =>
      getStudentCategoryAssignments(cat, student, settings),
    ),
  );
  return _.concat(studentInfoAndPointsColumns, realCats);
};

export const getAveragesRow = (
  student,
  categories,
  averages,
  leftGridColumnCount,
) => {
  const catAverages = _.flatten(
    _.map(categories, (cat) => getStudentCategoryAssignments(cat, student)),
  );
  const columnsWithAverages = catAverages.map((asgn) => {
    if (!asgn.isAggregateCell) {
      const averageData = averages.find((avg) => avg.assignmentId === asgn.id);
      return {
        ...averageData,
        isFirstCell: asgn.isFirstCell,
        gradebook_category_id: asgn.gradebook_category_id,
        missing: !averageData,
        isUngraded: asgn.isUngraded,
        isReefAssignment: asgn.tool === getToolIds().reef,
      };
    }
    return asgn;
  });

  for (let i = 0; i < leftGridColumnCount - 1; i++) {
    columnsWithAverages.unshift({ isEmptyCell: true });
  }
  columnsWithAverages.unshift({ isFirstCell: true, title: 'Average' });

  return columnsWithAverages;
};

// buildGradebookGridData builds the data structure for the instructor gradebook grid
// It's an array of arrays
// Each array withing the array is considered a row.
// Each array row contains 0 or more objects
// Those objects are the data for each cell within the table
// [
//    [{celldata}, {celldata}...]
//    [{celldata}, {celldata}...]
// ]
const buildGradebookGridData = ({
  categories,
  weights,
  assignments,
  students: studentsOrig,
  averages,
  settings,
}) => {
  let leftGridColumnCount = 2;
  const requireStudentId = extractSettingAs(
    settings,
    'requireStudentId',
    ({ value }) => value,
  );
  const isStudentIdRequired = requireStudentId === 'true';
  if (isStudentIdRequired) leftGridColumnCount = 3;

  // set all the ungraded assignments to have a category id of -1 (we can enforce this in the db once more of this epic has been completed)
  const students = _.map(studentsOrig, (s) => ({
    ...s,
    assignments: _.map(s.assignments, (a) => ({
      ...a,
      isUngraded: a.grading_type === GRADING_TYPE.UNGRADED,
    })),
    unfilteredAssignments: _.map(s.assignments, (a) => ({
      ...a,
      isUngraded: a.grading_type === GRADING_TYPE.UNGRADED,
    })),
  }));
  const firstStudent = _.first(students);
  const hasStudents = !!firstStudent;
  const rows = students.map((student) =>
    getStudentsRow({
      categories,
      weights,
      student,
      requireStudentId,
      settings,
    }),
  );
  const rowCount = Array.isArray(rows) && rows.length;
  const { assignmentsRow, hasAssignments } = getAssignmentsRow(
    categories,
    assignments,
    firstStudent,
    requireStudentId,
  );
  const categoryRow = getCategoriesRow(
    assignmentsRow,
    weights,
    leftGridColumnCount,
  );
  const averagesRow = getAveragesRow(
    firstStudent,
    categories,
    averages,
    leftGridColumnCount,
  );
  const columnCount = _.get(rows, '[0].length', 0);
  const isGradebookEmpty = !hasAssignments && !hasStudents;
  rows.unshift(categoryRow, assignmentsRow, averagesRow);
  return {
    rows,
    rowCount,
    columnCount,
    leftGridColumnCount,
    isStudentIdRequired,
    isGradebookEmpty,
    hasAssignments,
    hasStudents,
  };
};

// WARNING: This operation is expensive and should
// only recompute when absolutely necessary
const getGradebookRows = createSelector(
  [
    getGradebookStudents,
    getGradebookCategories,
    getGradebookCategoryWeights,
    getGradebookAssignments,
    getGradebookAverages,
    getGradebookSettings,
  ],
  (students, categories, weights, assignments, averages, settings) => {
    return buildGradebookGridData({
      categories,
      weights,
      assignments,
      students,
      averages,
      settings,
    });
  },
);

export const getGradebookGridDataSelector = (
  gradebookGridData,
  collapsedCategoryIds,
) => {
  const isCollapsed =
    Array.isArray(collapsedCategoryIds) && collapsedCategoryIds.length;

  if (isCollapsed) {
    return {
      ...gradebookGridData,
      rows: gradebookGridData.rows.map((row, rowIndex) =>
        row.filter((cell) => {
          const isCollapsedCategory = collapsedCategoryIds.includes(
            cell.gradebook_category_id,
          );

          if (isCollapsedCategory && rowIndex === 0) {
            return cell.isFirstCell;
          } else if (isCollapsedCategory && rowIndex > 0) {
            return cell.isAggregateCell;
          }
          return true;
        }),
      ),
    };
  }
  return gradebookGridData;
};

// This selector will only recompute when the
// following changes happen and will not recompute getGradebookRows
// - gradebookGridData
// - collapsedCategoryIds
const getGradebookGridData = createSelector(
  [getGradebookRows, getCollapsedCategoryIds],
  (gradebookGridData, collapsedCategoryIds) =>
    getGradebookGridDataSelector(gradebookGridData, collapsedCategoryIds),
);

export default getGradebookGridData;
